import { useEffect, useRef, type Ref } from 'react';
import intlTelInput, { type Iti } from 'intl-tel-input';
import en from 'intl-tel-input/i18n/en';
import ru from 'intl-tel-input/i18n/ru';
import 'intl-tel-input/styles';
import type { WidgetLang } from '../../lib/widget-translations';
import { normalizeInternationalPhoneValue } from './lib/normalize-international-phone';
import styles from './widget-preview.module.scss';

interface InternationalPhoneInputProps {
  id?: string;
  value: string;
  lang: WidgetLang;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  inputRef?: Ref<HTMLInputElement>;
  onChange: (value: string, isValid: boolean) => void;
}

export const InternationalPhoneInput = ({
  id,
  value,
  lang,
  placeholder,
  disabled,
  required,
  ariaInvalid,
  ariaDescribedBy,
  inputRef,
  onChange,
}: InternationalPhoneInputProps) => {
  const elementRef = useRef<HTMLInputElement | null>(null);
  const instanceRef = useRef<Iti | null>(null);
  const isSettingNumberRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const input = elementRef.current;
    if (!input) return;

    const root = input.getRootNode();
    const dropdownContainer =
      root instanceof ShadowRoot
        ? root.querySelector<HTMLElement>('[data-echo-widget-mount]')
        : document.body;
    const instance = intlTelInput(input, {
      containerClass: styles.internationalPhone,
      dropdownContainer: dropdownContainer ?? undefined,
      formatAsYouType: true,
      i18n: lang === 'ru' ? ru : en,
      initialCountry: '',
      loadUtils: () => import('intl-tel-input/utils'),
      nationalMode: false,
      separateDialCode: false,
      strictMode: true,
      useFullscreenPopup: false,
    });
    instanceRef.current = instance;

    const setCaretToEnd = () => {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    };

    const moveCaretToEnd = () => requestAnimationFrame(setCaretToEnd);

    const setNumber = (nextValue: string) => {
      isSettingNumberRef.current = true;
      instance.setNumber(nextValue);
      isSettingNumberRef.current = false;
      moveCaretToEnd();
    };

    const emitValue = () => {
      const hasNumber = /\d/.test(input.value);
      const nextValue = hasNumber
        ? instance.getNumber() || normalizeInternationalPhoneValue(input.value)
        : '';
      onChangeRef.current(nextValue, hasNumber && instance.isValidNumber() === true);
    };

    const handleInput = () => {
      if (isSettingNumberRef.current) {
        emitValue();
        return;
      }

      const normalizedValue = normalizeInternationalPhoneValue(input.value);
      if (normalizedValue !== input.value) {
        setNumber(normalizedValue);
        return;
      }

      emitValue();
    };

    const ensureSelectedCountryCode = () => {
      if (isSettingNumberRef.current || /\d/.test(input.value)) return false;

      const { dialCode } = instance.getSelectedCountryData();
      if (!dialCode) return false;

      setNumber(`+${dialCode}`);
      return true;
    };

    const handleCountryChange = () => {
      if (!ensureSelectedCountryCode() && !isSettingNumberRef.current) emitValue();
    };

    const handleFocus = () => {
      if (!input.value) setNumber('+');
    };

    const handleBlur = () => {
      if (input.value === '+') setNumber('');
    };

    const positionCountryDropdown = () => {
      const dropdownId = input.parentElement
        ?.querySelector<HTMLElement>('.iti__selected-country')
        ?.getAttribute('aria-controls');
      if (!dropdownId) return;

      const rootNode = input.getRootNode();
      const dropdownContent =
        rootNode instanceof ShadowRoot
          ? rootNode.getElementById(dropdownId)
          : input.ownerDocument.getElementById(dropdownId);
      if (!dropdownContent) return;

      const dropdown = dropdownContent.closest<HTMLElement>('.iti--container');
      const countryList = dropdownContent.querySelector<HTMLElement>('.iti__country-list');
      if (!dropdown || !countryList) return;

      countryList.style.maxHeight = '';
      const viewportTop = window.visualViewport?.offsetTop ?? 0;
      const viewportBottom = viewportTop + (window.visualViewport?.height ?? window.innerHeight);
      const inputRect = input.getBoundingClientRect();
      const dropdownOffsetTop =
        dropdownContent.getBoundingClientRect().top - dropdown.getBoundingClientRect().top;
      const gap = 4;
      const spaceAbove = Math.max(0, inputRect.top - viewportTop - gap);
      const spaceBelow = Math.max(0, viewportBottom - inputRect.bottom - gap);
      const openAbove = spaceAbove > spaceBelow;
      const availableHeight = openAbove ? spaceAbove : spaceBelow;
      const fixedHeight = dropdownContent.offsetHeight - countryList.offsetHeight;

      if (dropdownContent.offsetHeight > availableHeight) {
        countryList.style.maxHeight = `${Math.max(96, availableHeight - fixedHeight)}px`;
      }

      dropdown.style.top = openAbove
        ? `${Math.max(
            viewportTop + gap - dropdownOffsetTop,
            inputRect.top - dropdownContent.offsetHeight - dropdownOffsetTop - gap,
          )}px`
        : `${inputRect.bottom - dropdownOffsetTop + gap}px`;
    };

    const handleOpenCountryDropdown = positionCountryDropdown;

    const moveCaretPastCountryCode = () => {
      const { dialCode } = instance.getSelectedCountryData();
      const countryCode = dialCode ? `+${dialCode}` : '';
      const selectionStart = input.selectionStart ?? input.value.length;

      if (
        countryCode &&
        input.value.startsWith(countryCode) &&
        selectionStart < countryCode.length
      ) {
        setCaretToEnd();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (/^\d$/.test(event.key)) moveCaretPastCountryCode();
    };

    input.addEventListener('input', handleInput);
    input.addEventListener('countrychange', handleCountryChange);
    input.addEventListener('open:countrydropdown', handleOpenCountryDropdown);
    input.addEventListener('close:countrydropdown', ensureSelectedCountryCode);
    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);
    input.addEventListener('keydown', handleKeyDown);
    input.addEventListener('paste', moveCaretPastCountryCode);
    window.visualViewport?.addEventListener('resize', positionCountryDropdown);
    void instance.promise.then(emitValue).catch(() => undefined);

    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('countrychange', handleCountryChange);
      input.removeEventListener('open:countrydropdown', handleOpenCountryDropdown);
      input.removeEventListener('close:countrydropdown', ensureSelectedCountryCode);
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
      input.removeEventListener('keydown', handleKeyDown);
      input.removeEventListener('paste', moveCaretPastCountryCode);
      window.visualViewport?.removeEventListener('resize', positionCountryDropdown);
      instance.destroy();
      instanceRef.current = null;
    };
  }, [lang]);

  useEffect(() => instanceRef.current?.setDisabled(Boolean(disabled)), [disabled]);

  return (
    <input
      ref={(element) => {
        elementRef.current = element;
        if (typeof inputRef === 'function') inputRef(element);
        else if (inputRef) inputRef.current = element;
      }}
      id={id}
      type="tel"
      inputMode="tel"
      defaultValue={normalizeInternationalPhoneValue(value)}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
    />
  );
};
